import { Box, Card, CardContent, CardHeader, Grid } from '@mui/material';
import { t } from 'i18next';
import { useState } from 'react';
import { EXTERNAL_URLS } from '../../../routing/routes';
import { UrlHelper } from '../../utils/UrlHelper';
import { InventoryDataExtractor } from '../extractors/InventoryDataExtractor';
import { InventoryRecap } from '../models/InventoryRecap';
import { getHelpTextMap } from '../utils';
import { TabChart } from './TabChart';
import { TitleChart } from './TitleChart';

type CardProps = Props & {
  title: string;
  subtitle: string;
};

type Props = {
  inventory: InventoryRecap | null;
  loadingData: boolean;
  initialLoad: boolean;
};

const GRID_ITEM_SMALL_PROPS = { item: true, xl: 4, md: 6, xs: 12, sx: { '& > .MuiPaper-root': { height: 1, '& > .MuiBox-root': { height: 1 } } } };
const GRID_ITEM_MEDIUM_PROPS = { ...GRID_ITEM_SMALL_PROPS, xl: 8, md: 12 };

export const InventoryCard = ({ inventory, loadingData, initialLoad, title, subtitle }: CardProps) => {
  if (!initialLoad && !loadingData && (!inventory || inventory.isEmpty())) {
    return null;
  }

  return (
    <Card data-role="inventory-card-container">
      <CardHeader
        title={title}
        titleTypographyProps={{ component: 'h2', variant: 'h4' }}
        subheader={subtitle}
        subheaderTypographyProps={{ component: 'p', variant: 'subtitle1' }}
      />
      <CardContent>
        <Inventory inventory={inventory} loadingData={loadingData} initialLoad={initialLoad} />
      </CardContent>
    </Card>
  );
};

export const Inventory = ({ inventory, loadingData, initialLoad }: Props) => {
  const [extractor] = useState(new InventoryDataExtractor());

  const { keyStores, hashFunctions, mac, symmetricAlgorithms, asymmetricAlgorithms, operations, keys } = extractor.extractInventoryElements(inventory);

  return (
    <Box data-role="inventory-container">
      <Grid container spacing={2}>
        {(initialLoad || (keys && keys.length > 0)) && (
          <Grid {...GRID_ITEM_MEDIUM_PROPS}>
            <TabChart title={t('keys')} categories={keys} helpMessages={getHelpTextMap()} loading={loadingData} initialLoad={initialLoad} />
          </Grid>
        )}
        {(initialLoad || operations) && (
          <Grid {...GRID_ITEM_SMALL_PROPS}>
            <TitleChart category={operations} title={t('operations')} helpText={t('helpTexts.operations')} loading={loadingData} initialLoad={initialLoad} />
          </Grid>
        )}
        {(initialLoad || symmetricAlgorithms) && (
          <Grid {...GRID_ITEM_SMALL_PROPS}>
            <TitleChart
              category={symmetricAlgorithms}
              title={t('symmetricAlgorithms')}
              helpText={t('helpTexts.symmetricAlgorithms')}
              loading={loadingData}
              initialLoad={initialLoad}
            />
          </Grid>
        )}
        {(initialLoad || asymmetricAlgorithms) && (
          <Grid {...GRID_ITEM_SMALL_PROPS}>
            <TitleChart
              category={asymmetricAlgorithms}
              title={t('asymmetricAlgorithms')}
              helpText={t('helpTexts.asymmetricAlgorithms')}
              loading={loadingData}
              initialLoad={initialLoad}
            />
          </Grid>
        )}
        {(initialLoad || keyStores) && (
          <Grid {...GRID_ITEM_SMALL_PROPS}>
            <TitleChart
              category={keyStores}
              title={t('keyStore')}
              helpText={t('helpTexts.keyStores')}
              loading={loadingData}
              initialLoad={initialLoad}
              linkTo={UrlHelper.wrapKnowledgeBaseUrl(EXTERNAL_URLS.DOCS_KB_JAVA_KEYSTORES)}
            />
          </Grid>
        )}
        {(initialLoad || hashFunctions) && (
          <Grid {...GRID_ITEM_SMALL_PROPS}>
            <TitleChart
              category={hashFunctions}
              title={t('hashFunctions')}
              loading={loadingData}
              initialLoad={initialLoad}
              helpText={t('helpTexts.hashFunctions')}
              linkTo={UrlHelper.wrapKnowledgeBaseUrl(EXTERNAL_URLS.DOCS_KB_JAVA_HASH_FUNCTIONS)}
            />
          </Grid>
        )}
        {(initialLoad || mac) && (
          <Grid {...GRID_ITEM_SMALL_PROPS}>
            <TitleChart category={mac} title={t('mac')} helpText={t('helpTexts.mac')} loading={loadingData} initialLoad={initialLoad} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
